import React, { useMemo } from "react";
import { ArrowLongRightIcon, CalendarDaysIcon, ErrorMessage, Select } from "@";
import DatePicker, { registerLocale } from "react-datepicker";
import { arSA } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "@/assets/react-datepicker-theme.css";

// Register Arabic (Saudi Arabia) once at module load so any DatePicker
// using locale="ar-SA" picks up Arabic month/day names.
registerLocale("ar-SA", arSA);

// Function to render the label with a star if required
const renderLabel = (label, star, disabled) => (
  <label className="input-label">
    {label}
    {!star && !disabled && <span className="star">*</span>}
  </label>
);

// Function to render error messages
const renderErrorMessage = (error) =>
  error && <ErrorMessage value={error?.message} />;

const Input = React.memo((props) => {
  const {
    id,
    dir,
    min,
    max,
    type,
    star,
    list,
    label,
    value,
    input,
    keyUp,
    error,
    style,
    labelA,
    valueA,
    errorA,
    labelB,
    valueB,
    errorB,
    styles,
    changeA,
    changeB,
    options,
    change,
    required,
    disabled,
    children,
    maxLength,
    className,
    placeholder,
    placeholderA,
    placeholderB,
    noEmptyOption,
    showTimeSelect,
    showTimeOnly,
    dateFormat,
    description,
  } = props;

  // Function to render a textarea input
  const renderTextArea = useMemo(
    () => () => (
      <textarea
        {...input}
        value={value}
        onKeyUp={keyUp}
        className="input resize-y"
        onChange={change}
        dir={dir || "rtl"}
        required={required}
        rows={props.rows || 4}
        placeholder={placeholder}
        disabled={disabled ? "disabled" : ""}
      ></textarea>
    ),
    [
      input,
      value,
      keyUp,
      dir,
      required,
      props.rows,
      placeholder,
      disabled,
      change,
    ],
  );

  // Function to render a select input
  const renderSelect = useMemo(
    () => () => {
      // Build a unified option list from `list`, JSX `options`, and JSX
      // `children` (legacy `<option>` element pattern). React-select needs a
      // flat `{ value, label }[]`, so JSX <option>s are extracted here.
      const optionList = [];

      if (Array.isArray(list)) {
        for (const item of list) {
          optionList.push({
            value: item.id ?? item.value,
            label: item.name ?? item.title ?? item.giftProjetName ?? item.label,
          });
        }
      }

      const collectFromJsx = (nodes) => {
        React.Children.forEach(nodes, (child) => {
          if (!React.isValidElement(child)) return;
          if (child.type !== "option") return;
          if (child.props?.value === undefined || child.props?.value === "")
            return;
          optionList.push({
            value: child.props.value,
            label: child.props.children,
          });
        });
      };
      collectFromJsx(options);
      collectFromJsx(children);

      const currentValue =
        optionList.find((opt) => String(opt.value) === String(value ?? "")) ??
        null;

      // Bridge react-select's `(option)` callback back to the legacy
      // `(syntheticEvent)` shape so existing handlers (`e.target.value`)
      // and react-hook-form `register` integration both keep working.
      const handleChange = (opt) => {
        const nextValue = opt?.value ?? "";
        const event = { target: { name: input?.name, value: nextValue } };
        if (typeof change === "function") change(event);
        if (input && typeof input.onChange === "function")
          input.onChange(event);
      };

      return (
        <div className="relative">
          {/* Hidden input keeps RHF `register` integration alive: RHF
              attaches its ref here and reads/writes the field value. */}
          {input && (
            <input
              type="hidden"
              name={input.name}
              ref={input.ref}
              value={currentValue?.value ?? ""}
              onChange={() => {}}
            />
          )}
          <Select
            inputId={id}
            value={currentValue}
            options={optionList}
            onChange={handleChange}
            onBlur={input?.onBlur}
            isDisabled={!!disabled}
            isClearable={!noEmptyOption}
            placeholder={placeholder ?? ""}
            className={className}
          />
        </div>
      );
    },
    [
      id,
      input,
      value,
      className,
      change,
      disabled,
      noEmptyOption,
      options,
      children,
      list,
      placeholder,
    ],
  );

  // Function to render a date picker input
  const renderDatePicker = useMemo(
    () => () => (
      <div className="group/dp relative">
        <DatePicker
          todayButton="اليوم"
          showYearDropdown
          showMonthDropdown
          locale="ar-SA"
          selectsStart
          selected={value}
          startDate={value}
          minDate={props.minDate}
          maxDate={props.maxDate}
          timeFormat="HH:mm"
          timeIntervals={1}
          timeCaption="الوقت"
          onChange={change}
          shouldCloseOnSelect={true}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeOnly}
          className={`${styles ? styles : "input"} pe-10`}
          disabled={disabled ? "disabled" : ""}
          dateFormat={
            dateFormat
              ? dateFormat
              : showTimeSelect
                ? "dd/MM/yyyy, HH:mm"
                : "dd/MM/yyyy"
          }
        />
        <span
          aria-hidden
          className="pointer-events-none absolute end-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-purple-700 transition-colors group-focus-within/dp:bg-purple-50 group-focus-within/dp:text-purple-900 dark:text-purple-300 dark:group-focus-within/dp:bg-purple-950/40"
        >
          <CalendarDaysIcon className="h-5 w-5" />
        </span>
        <input type="hidden" {...input} />
      </div>
    ),
    [
      value,
      change,
      showTimeSelect,
      styles,
      disabled,
      input,
      showTimeOnly,
      dateFormat,
      props.minDate,
      props.maxDate,
    ],
  );

  // Function to render a date range input
  const renderDateRange = useMemo(
    () => () => (
      <div
        className={`${
          showTimeSelect
            ? "flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-end"
            : "flex items-end gap-1.5"
        } `}
      >
        <div className="group/dp relative w-full">
          {labelA && renderLabel(labelA, star, disabled)}
          <DatePicker
            todayButton="اليوم"
            showYearDropdown
            showMonthDropdown
            locale="ar-SA"
            selected={valueA}
            timeFormat="HH:mm"
            timeIntervals={1}
            timeCaption="الوقت"
            onChange={changeA}
            startDate={valueA}
            className="input w-full pe-10"
            shouldCloseOnSelect={true}
            placeholderText={placeholderA}
            showTimeSelect={showTimeSelect}
            disabled={disabled ? "disabled" : ""}
            dateFormat={showTimeSelect ? "dd/MM/yyyy, HH:mm" : "dd/MM/yyyy"}
          />
          <span
            aria-hidden
            style={{ top: labelA ? "34px" : "6px" }}
            className="pointer-events-none absolute end-2 flex h-7 w-7 items-center justify-center rounded-md text-purple-700 dark:text-purple-300"
          >
            <CalendarDaysIcon className="h-5 w-5" />
          </span>
          <input type="hidden" {...props.inputA} />
          {renderErrorMessage(errorA)}
        </div>

        {/* Connector arrow */}
        <span
          aria-hidden
          s
          className="hidden h-10 shrink-0 items-center justify-center text-gray-300 dark:text-gray-600 sm:flex"
        >
          <ArrowLongRightIcon className="h-5 w-5 rtl:rotate-180" />
        </span>

        <div className="group/dp relative w-full">
          {labelB && renderLabel(labelB, star, disabled)}
          <DatePicker
            todayButton="اليوم"
            showYearDropdown
            showMonthDropdown
            locale="ar-SA"
            minDate={valueA}
            selected={valueB}
            timeFormat="HH:mm"
            timeIntervals={1}
            timeCaption="الوقت"
            onChange={changeB}
            startDate={valueA}
            className="input w-full pe-10"
            shouldCloseOnSelect={true}
            placeholderText={placeholderB}
            showTimeSelect={showTimeSelect}
            disabled={disabled ? "disabled" : ""}
            dateFormat={showTimeSelect ? "dd/MM/yyyy, HH:mm" : "dd/MM/yyyy"}
          />
          <span
            aria-hidden
            style={{ top: labelB ? "34px" : "6px" }}
            className="pointer-events-none absolute end-2 flex h-7 w-7 items-center justify-center rounded-md text-purple-700 dark:text-purple-300"
          >
            <CalendarDaysIcon className="h-5 w-5" />
          </span>
          <input type="hidden" {...props.inputB} />
          {renderErrorMessage(errorB)}
        </div>
      </div>
    ),
    [
      showTimeSelect,
      labelA,
      star,
      disabled,
      valueB,
      valueA,
      changeA,
      placeholderA,
      errorA,
      props.inputA,
      labelB,
      changeB,
      placeholderB,
      errorB,
      props.inputB,
    ],
  );

  // Function to render a number range input
  const renderNumberRange = useMemo(
    () => () => (
      <div className="flex items-end gap-1.5">
        <div className="relative w-1/2">
          {labelA && renderLabel(labelA, star, disabled)}
          <input
            type="number"
            value={valueA}
            {...props.inputA}
            className="input"
            dir={dir || "rtl"}
            onChange={changeA}
            required={required}
            disabled={disabled ? "disabled" : ""}
            placeholder={placeholderA}
          />
          {renderErrorMessage(errorA)}
        </div>

        {/* Connector arrow */}
        <span
          aria-hidden
          className="hidden h-10 shrink-0 items-center justify-center text-gray-300 dark:text-gray-600 sm:flex"
        >
          <ArrowLongRightIcon className="h-5 w-5 rtl:rotate-180" />
        </span>

        <div className="relative w-1/2">
          {labelB && renderLabel(labelB, star, disabled)}
          <input
            type="number"
            value={valueB}
            {...props.inputB}
            className="input"
            dir={dir || "rtl"}
            onChange={changeB}
            required={required}
            disabled={disabled ? "disabled" : ""}
            placeholder={placeholderB}
          />
          {renderErrorMessage(errorB)}
        </div>
      </div>
    ),
    [
      labelA,
      star,
      disabled,
      valueA,
      props.inputA,
      changeA,
      required,
      errorA,
      labelB,
      valueB,
      props.inputB,
      changeB,
      errorB,
      dir,
      placeholderA,
      placeholderB,
    ],
  );

  // Function to render a generic input
  const renderInput = useMemo(
    () => () => (
      <input
        readOnly={disabled}
        id={id}
        min={min}
        max={max}
        {...input}
        value={value}
        onKeyUp={keyUp}
        dir={dir || "rtl"}
        onChange={change}
        required={required}
        maxLength={maxLength}
        type={type || "text"}
        placeholder={placeholder}
        autoComplete={props.autocomplete}
        className={styles ? styles : "input"}
        disabled={disabled ? "disabled" : ""}
      />
    ),
    [
      id,
      min,
      max,
      input,
      value,
      keyUp,
      dir,
      change,
      required,
      type,
      placeholder,
      props.autocomplete,
      styles,
      disabled,
    ],
  );

  return (
    <div className={style}>
      {label &&
        type !== "date-range" &&
        type !== "number-range" &&
        renderLabel(label, star, disabled)}
      {type === "textarea" && renderTextArea()}
      {type === "select" && renderSelect()}
      {type === "select-searchable" && (
        <Select
          isDisabled={disabled}
          value={value}
          placeholder={props.placeholder || ""}
          options={list}
          onChange={change}
          isClearable={true}
        />
      )}
      {type === "datepicker" && renderDatePicker()}
      {type === "date-range" && renderDateRange()}
      {type === "number-range" && renderNumberRange()}
      {type !== "textarea" &&
        type !== "select" &&
        type !== "select-searchable" &&
        type !== "datepicker" &&
        type !== "date-range" &&
        type !== "number-range" &&
        renderInput()}
      {error &&
        type !== "date-range" &&
        type !== "number-range" &&
        renderErrorMessage(error)}
      {description && <p className="text-gray-500 mt-1 text-xs">{description}</p>}
    </div>
  );
});

export default Input;
